import { useQuery } from '@tanstack/react-query'
import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroTypes'
import { toIsoDate } from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.mapping'
import type {
  CardManagementNextPaymentDueStatus,
  CardManagementStatementProvenance,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { isCardManagementActionAvailable } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

export type CardOverviewLimits = {
  /** Balance drawn on the current cycle. */
  balance: Money
  /** The card's permanent spend limit. */
  limit: Money
  /**
   * Balance as a percentage of the limit, 0–100.
   *
   * `undefined` when the limit is zero or negative — utilization against no
   * limit is undefined, NOT zero, and rendering 0% there would read as "plenty
   * of room" on a card that has none.
   */
  utilizationPercent: number | undefined
}

/**
 * Per-group load state, shared by all three sources and mirroring
 * `useCardDetailHeroData`'s vocabulary so the two card-management seams read the
 * same way.
 *
 * A denied capability is a settled answer about the card (`unavailable`), not a
 * stand-in for one, and stays distinct from a failed request (`error`). The
 * limits group had its own union with a `demo` member until EX-40596 deleted the
 * fabricated fallback; it now answers in exactly these terms like the others.
 */
export type CardOverviewStatsSourceState =
  | 'loading'
  | 'ready'
  | 'unavailable'
  | 'error'

export type CardOverviewPaymentDue = {
  amount: Money
  /** LocalDate, `"YYYY-MM-DD"`. */
  dueDate: string
  status: CardManagementNextPaymentDueStatus
}

export type CardOverviewBillingCycle = {
  /** LocalDate, `"YYYY-MM-DD"` — the statement-close date. */
  periodEnd: string
}

export type CardOverviewStatsData = {
  /** `undefined` unless `limitsState` is `ready`. Always real when set. */
  limits: CardOverviewLimits | undefined
  limitsState: CardOverviewStatsSourceState
  /**
   * `undefined` unless `paymentState` is `ready`. `null` is itself a real
   * `ready` answer — "nothing currently due" — and must not be treated as a
   * gap; see `CardManagementPaymentSummaryResponse`.
   */
  payment: CardOverviewPaymentDue | null | undefined
  paymentState: CardOverviewStatsSourceState
  /** Set only once `paymentState` is `ready`. */
  paymentProvenance: CardManagementStatementProvenance | undefined
  /** `undefined` unless `billingCycleState` is `ready`. */
  billingCycle: CardOverviewBillingCycle | undefined
  billingCycleState: CardOverviewStatsSourceState
  /** Set only once `billingCycleState` is `ready`. */
  billingCycleProvenance: CardManagementStatementProvenance | undefined
}

/** Exported for tests; not part of the seam's contract. */
export const utilizationPercentOf = (
  balanceMinor: number,
  limitMinor: number,
): number | undefined =>
  limitMinor > 0 ? (balanceMinor / limitMinor) * 100 : undefined

/**
 * Shared state derivation for all three groups. A denied capability resolves to
 * `unavailable` and a failed request to `error`; nothing collapses them, because
 * there is no fabricated figure to show in either case.
 */
const sourceStateOf = (
  capabilitiesFailed: boolean,
  allowed: boolean | undefined,
  query: { data: unknown; isError: boolean },
): CardOverviewStatsSourceState => {
  if (capabilitiesFailed) return 'error'
  if (allowed === undefined) return 'loading'
  if (!allowed) return 'unavailable'
  if (query.data !== undefined) return 'ready'
  return query.isError ? 'error' : 'loading'
}

/**
 * Single data seam for the card Overview stats roll-up (EX-40632).
 *
 * The roll-up's sections MUST read through this hook rather than reaching for a
 * query directly, so wiring a new endpoint touches this file and nothing else.
 *
 * Grouped by SOURCE, not by consumer: limits, payment summary, and the current
 * billing cycle are three independently fetched endpoints with their own failure
 * windows, each gated on its own capability. Every figure this seam returns is
 * real — a group that cannot be read returns `undefined` with a state saying
 * why, and the roll-up renders an em dash rather than a stand-in.
 *
 * NOT here, deliberately: the virtual-card and budget counts. Those live in the
 * legacy per-credit-card statistics family, whose selectors are route-coupled
 * (`selectCreditCardIdFromRoute`), and features must not read routing. They also
 * cannot be re-fetched here safely — the paginated credit-card query dispatches
 * `GET_CREDIT_CARDS_INIT`, which flips `fetchingCreditCards` in four unrelated
 * reducers. The mounting page passes them as props instead, exactly as the
 * legacy `AccountOverview` already does.
 *
 * @param creditCardId - The card whose limits to read. A required argument
 *   rather than something this hook resolves: the roll-up is a feature, and the
 *   page owns the URL.
 *
 *   Required in the type as well as in spirit. An absent id disables the
 *   capability query, and a disabled query neither resolves nor errors — so
 *   `limitsAllowed` would stay `undefined` and this hook would report `loading`
 *   with no terminal state, holding `aria-busy` on the roll-up forever. There
 *   is no sensible answer to give without an id, so the type refuses the
 *   question.
 */
export const useCardOverviewStatsData = (
  creditCardId: string,
): CardOverviewStatsData => {
  // `retry: false` comes from the factory and is deliberate — a denied
  // capability is an answer, not a flake.
  const capabilitiesQuery = useQuery(
    physicalCardManagementQueries.capabilities(creditCardId),
  )

  // Deliberately keyed off `capabilitiesQuery.data` rather than passing the
  // capabilities straight through: the shared helper collapses "no verdict yet"
  // and "verdict says no" into the same `false`, and this seam needs to tell
  // them apart — `undefined` is what holds `limitsState` at `loading` instead of
  // reporting a denial before the verdict has arrived.
  const limitsAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilitiesQuery.data.capabilities,
        'READ_PHYSICAL_CARD_LIMIT',
      )
    : undefined

  const limitsQuery = useQuery({
    ...physicalCardManagementQueries.limits(creditCardId),
    // A spread override REPLACES the factory's arg-presence guard rather than
    // merging with it, so it is re-stated here. Declarative `enabled` rather
    // than an effect: a denied capability must never fire a request the backend
    // will reject.
    enabled: Boolean(creditCardId) && limitsAllowed === true,
  })

  // EX-40596 resolved the former TODO here: a denied `READ_PHYSICAL_CARD_LIMIT`
  // is `unavailable` and a failed request is `error`. They shared a `demo` state
  // only because there were fabricated figures to show either way, and there no
  // longer are.
  const limitsState = sourceStateOf(
    capabilitiesQuery.isError,
    limitsAllowed,
    limitsQuery,
  )

  const limitsResponse = limitsQuery.data

  // Same "no verdict yet" vs "verdict says no" distinction as `limitsAllowed`
  // above, so a pending capability check cannot flash `unavailable` before the
  // real verdict arrives.
  const paymentsAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilitiesQuery.data.capabilities,
        'READ_PHYSICAL_CARD_PAYMENTS',
      )
    : undefined

  const paymentSummaryQuery = useQuery({
    ...physicalCardManagementQueries.paymentSummary(creditCardId),
    // Restated for the same reason as `limitsQuery.enabled`: a spread override
    // replaces the factory's arg-presence guard rather than merging with it.
    enabled: Boolean(creditCardId) && paymentsAllowed === true,
  })

  const paymentState = sourceStateOf(
    capabilitiesQuery.isError,
    paymentsAllowed,
    paymentSummaryQuery,
  )

  const paymentResponse = paymentSummaryQuery.data

  // `null` is `ready`'s own answer — "nothing currently due" — never a gap.
  const payment: CardOverviewPaymentDue | null | undefined =
    paymentState !== 'ready' || paymentResponse === undefined
      ? undefined
      : paymentResponse.nextPaymentDue === null
        ? null
        : {
            amount: {
              amountMinor: paymentResponse.nextPaymentDue.amountDue,
              // The payment's OWN currency, not the card's limits currency.
              currency: paymentResponse.nextPaymentDue.currency,
            },
            // The wire carries `[year, month, day]`; the hook is the seam that
            // normalizes it to the ISO string the stat formatters take.
            dueDate: toIsoDate(paymentResponse.nextPaymentDue.dueDate),
            status: paymentResponse.nextPaymentDue.status,
          }

  const paymentProvenance =
    paymentState === 'ready' ? paymentResponse?.provenance : undefined

  const statementsAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilitiesQuery.data.capabilities,
        'READ_PHYSICAL_CARD_STATEMENTS',
      )
    : undefined

  const currentCycleQuery = useQuery({
    ...physicalCardManagementQueries.currentCycle(creditCardId),
    enabled: Boolean(creditCardId) && statementsAllowed === true,
  })

  const billingCycleState = sourceStateOf(
    capabilitiesQuery.isError,
    statementsAllowed,
    currentCycleQuery,
  )

  const currentCycleResponse = currentCycleQuery.data

  const billingCycle: CardOverviewBillingCycle | undefined =
    billingCycleState === 'ready' && currentCycleResponse !== undefined
      ? { periodEnd: toIsoDate(currentCycleResponse.periodEnd) }
      : undefined

  const billingCycleProvenance =
    billingCycleState === 'ready' ? currentCycleResponse?.provenance : undefined

  return {
    limits:
      limitsState === 'ready' && limitsResponse !== undefined
        ? {
            balance: {
              amountMinor: limitsResponse.currentBalance,
              currency: limitsResponse.currency,
            },
            limit: {
              amountMinor: limitsResponse.permanentLimit,
              currency: limitsResponse.currency,
            },
            utilizationPercent: utilizationPercentOf(
              limitsResponse.currentBalance,
              limitsResponse.permanentLimit,
            ),
          }
        : undefined,
    limitsState,
    payment,
    paymentState,
    paymentProvenance,
    billingCycle,
    billingCycleState,
    billingCycleProvenance,
  }
}
